import { Box, ConfidenceTicks, Text } from '@grovus/design-system'
import type { Dict } from '../i18n'
import type { ConfidenceThresholds } from '../creatives/confidence'

export type Screen = 'creatives' | 'rollups' | 'members' | 'invitations' | 'permission' | 'share' | 'dictionary' | 'settings'

interface Props {
  t: Dict
  /** The company's tiers from the shell's read (GRO-186); `null` before the first one lands. */
  thresholds: ConfidenceThresholds | null
  onSavedView: (name: 'weekly' | 'thin' | 'theme') => void
}

/**
 * The sidebar content below the primary nav, which AppShell renders itself via
 * SidebarNav: saved views and the reliability legend. Reuses the design system's
 * ConfidenceTicks for the legend rather than a local ticks glyph, with a
 * conversions count chosen per row to land it in the band that row names.
 */
export function Sidebar({ t, thresholds, onSavedView }: Props) {
  const saved: [string, 'weekly' | 'thin' | 'theme'][] = [
    [t.svWeekly, 'weekly'],
    [t.svThin, 'thin'],
    [t.svTheme, 'theme'],
  ]

  // No rows before the first read, so the legend never shows default tiers that the
  // company's then replace. A row whose range is empty, two tiers being equal, is left out.
  const legend: [string, number][] = thresholds === null ? [] : [
    [`${t.relReliable} - n ≥ ${thresholds.reliableAt}`, thresholds.reliableAt],
    ...(thresholds.provisionalAt < thresholds.reliableAt
      ? [[`${t.relProvisional} - n ${range(thresholds.provisionalAt, thresholds.reliableAt - 1)}`, thresholds.provisionalAt] as [string, number]]
      : []),
    ...(thresholds.directionAt < thresholds.provisionalAt
      ? [[`${t.relDirection} - n ${range(thresholds.directionAt, thresholds.provisionalAt - 1)}`, thresholds.directionAt] as [string, number]]
      : []),
  ]

  return (
    <>
      <Box mt="md" px="md">
        <Text fz={10} tt="uppercase" className="grv-muted" style={{ letterSpacing: '0.12em' }} mb={8}>{t.savedViews}</Text>
        {saved.map(([label, key]) => (
          <button key={key} type="button" className="app-shell-link-button" onClick={() => onSavedView(key)}>
            {label}
          </button>
        ))}
      </Box>

      <Box mt="auto" p="md" style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
        <Text fz={10} tt="uppercase" className="grv-muted" style={{ letterSpacing: '0.12em' }} mb={8}>{t.reliability}</Text>
        {legend.map(([label, conversions]) => (
          <Box key={label} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', fontSize: 11 }}>
            <ConfidenceTicks conversions={conversions} thresholds={thresholds ?? undefined} aria-label={label} />
            <Text fz={11} className="grv-muted">{label}</Text>
          </Box>
        ))}
        <Text fz={10} mt={7} className="grv-muted" style={{ lineHeight: 1.45 }}>{t.legendNote}</Text>
      </Box>
    </>
  )
}

function range(lo: number, hi: number): string {
  return lo === hi ? `${lo}` : `${lo}-${hi}`
}
